type DismissedAlert = {
  guid: string;
  hash: string;
};

type AlertBanner = {
  cookieName: string;
  maxDismissedAlerts: number;
  init: () => void;
  applyDismissed: () => void;
  attachDismissHandlers: () => void;
  getCookie: (name: string) => string | null;
  setCookie: (name: string, value: string) => void;
  getDismissedAlerts: () => DismissedAlert[];
  addDismissedAlert: (guid: string, hash: string) => void;
};

const AlertBanner: AlertBanner = {
  cookieName: 'rsfh_alert_banners_dismissed',
  maxDismissedAlerts: 50,

  init() {
    this.applyDismissed();
    this.attachDismissHandlers();
  },

  /**
   * On load: hide any alert whose GUID and content hash both match a stored dismissed entry.
   * Stores multiple dismissed alerts as a JSON array in a single cookie to handle concurrent banners.
   */
  applyDismissed() {
    const dismissed = this.getDismissedAlerts();
    if (dismissed.length === 0) return;

    const dismissedMap = new Map(dismissed.map(d => [`${d.guid}:${d.hash}`, true]));

    const banners = document.querySelectorAll<HTMLElement>('[data-hook="alertBanner"]');
    banners.forEach((banner) => {
      const bannerGuid = banner.getAttribute('data-alert-guid');
      const bannerHash = banner.getAttribute('data-alert-hash');

      if (bannerGuid && bannerHash && dismissedMap.has(`${bannerGuid}:${bannerHash}`)) {
        banner.style.display = 'none';
        banner.setAttribute('aria-hidden', 'true');
      }
    });
  },

  /**
   * Attach click handlers to all dismiss buttons.
   * On click: store the alert's GUID and content hash in the dismissed collection, then hide the banner.
   */
  attachDismissHandlers() {
    const dismissButtons = document.querySelectorAll<HTMLElement>('[data-hook="alertBanner__dismiss"]');
    dismissButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const banner = button.closest<HTMLElement>('[data-hook="alertBanner"]');
        if (!banner) return;

        const guid = banner.getAttribute('data-alert-guid');
        const hash = banner.getAttribute('data-alert-hash');

        if (guid && hash) {
          this.addDismissedAlert(guid, hash);
        }

        banner.style.display = 'none';
        banner.setAttribute('aria-hidden', 'true');
      });
    });
  },

  getDismissedAlerts(): DismissedAlert[] {
    const cookie = this.getCookie(this.cookieName);
    if (!cookie) return [];
    try {
      return JSON.parse(decodeURIComponent(cookie));
    } catch {
      return [];
    }
  },

  addDismissedAlert(guid: string, hash: string) {
    const dismissed = this.getDismissedAlerts();
    
    // Avoid duplicates
    if (dismissed.some(d => d.guid === guid && d.hash === hash)) {
      return;
    }

    // Evict oldest if at max capacity
    if (dismissed.length >= this.maxDismissedAlerts) {
      dismissed.shift();
    }

    dismissed.push({ guid, hash });
    this.setCookie(this.cookieName, encodeURIComponent(JSON.stringify(dismissed)));
  },

  getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? match[1] : null;
  },

  setCookie(name: string, value: string) {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${value}; path=/; SameSite=Lax${secure}`;
  }
};

export default AlertBanner;
